import { apiClient } from './api';
import type { ApiResponse } from '@/types';

export interface Contact {
  id: string;
  organization_id: string;
  name?: string;
  email?: string;
  phone?: string;
  preferred_language: string;
  metadata: Record<string, unknown>;
  linked_channels?: string[];
  conversations_count?: number;
  created_at: string;
  updated_at: string;
}

export interface LinkedCustomer {
  id: string;
  external_id?: string;
  channel_type?: string;
  name?: string;
  email?: string;
  phone?: string;
  verified: boolean;
  linked_by: string;
  created_at: string;
}

export interface PotentialMatch {
  customer: {
    id: string;
    external_id?: string;
    channel_type?: string;
    name?: string;
    email?: string;
    phone?: string;
  };
  contact: {
    id: string;
    name?: string;
  };
  matched_on: string;
}

export interface TimelineMessage {
  id: string;
  content: string;
  content_type: string;
  sender_type: string;
  channel_type: string;
  conversation_id: string;
  created_at: string;
}

export interface DocumentExtraction {
  id: string;
  organization_id: string;
  customer_id: string;
  contact_id?: string;
  conversation_id?: string;
  r2_key: string;
  filename: string;
  mime_type: string;
  file_size?: number;
  document_type?: string;
  extraction_status: 'pending' | 'processing' | 'completed' | 'failed';
  extracted_data: Record<string, unknown>;
  raw_text?: string;
  confidence_score?: number;
  confirmed_data?: Record<string, unknown>;
  confirmed_at?: string;
  error_message?: string;
  cost_usd?: number;
  input_tokens?: number;
  output_tokens?: number;
  model?: string;
  created_at: string;
  updated_at: string;
}

export interface ContactDetail {
  contact: Contact;
  linked_customers: LinkedCustomer[];
  unified_context: {
    channels: string[];
    last_interaction?: { channel: string; date: string; topic?: string };
    collected_data: Record<string, unknown>;
    conversation_count: number;
  } | null;
}

class ContactService {
  async list(params: { search?: string; limit?: number; offset?: number } = {}): Promise<{ contacts: Contact[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.set('search', params.search);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));

    const response = await apiClient.get<ApiResponse<{ contacts: Contact[]; total: number }>>(
      `/contacts?${searchParams.toString()}`
    );
    return response.data.data || { contacts: [], total: 0 };
  }

  async getById(id: string): Promise<ContactDetail> {
    const response = await apiClient.get<ApiResponse<ContactDetail>>(`/contacts/${id}`);
    if (response.data.data) return response.data.data;
    throw new Error('Contact not found');
  }

  async linkCustomer(contactId: string, customerId: string, reason: string): Promise<void> {
    await apiClient.post(`/contacts/${contactId}/link`, { customer_id: customerId, reason });
  }

  async unlinkCustomer(contactId: string, customerId: string, reason: string): Promise<void> {
    await apiClient.post(`/contacts/${contactId}/unlink`, { customer_id: customerId, reason });
  }

  async getPotentialMatches(params: { limit?: number; offset?: number } = {}): Promise<{ matches: PotentialMatch[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));

    const response = await apiClient.get<ApiResponse<{ matches: PotentialMatch[]; total: number }>>(
      `/contacts/potential-matches?${searchParams.toString()}`
    );
    return response.data.data || { matches: [], total: 0 };
  }
  async getTimeline(contactId: string, params: { limit?: number; offset?: number } = {}): Promise<{ messages: TimelineMessage[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));

    const response = await apiClient.get<ApiResponse<{ messages: TimelineMessage[]; total: number }>>(
      `/contacts/${contactId}/timeline?${searchParams.toString()}`
    );
    return response.data.data || { messages: [], total: 0 };
  }
  async getDocuments(contactId: string): Promise<DocumentExtraction[]> {
    const response = await apiClient.get<ApiResponse<DocumentExtraction[]>>(
      `/contacts/${contactId}/documents`
    );
    return response.data.data || [];
  }
}

export const contactService = new ContactService();
